/** Wire LoginPage to real auth backend. */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Lock, Mail } from "lucide-react";
import {
  Container,
  Section,
  Eyebrow,
  AccentItalic,
  Button,
  Input,
  FormField,
  ArtCard,
  Alert,
} from "../../components/ui";
import { BrandMark } from "../../components/ui/Brand";
import { useAuth, userIsAdmin } from "./AuthContext";
import { usePublicSettings } from "../../lib/usePublicSettings";

// Right-hand panel — editable in Admin → Settings → login_page.
function LoginSidePanel() {
  const settings = usePublicSettings();
  const c = settings?.login_page;
  if (!settings) return <div className="hidden lg:block rounded-[28px] border border-line-2 min-h-[640px] bg-surface" />;
  if (c && c.visible === false) return null;
  const p = c || {};
  return (
    <div className="relative hidden lg:block rounded-[28px] overflow-hidden border border-line-2 min-h-[640px]" data-testid="login-side-panel">
      {p.image_url ? (
        <>
          <img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="login-side-image" />
          <div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-t from-black/75 to-transparent" />
        </>
      ) : (
        <ArtCard seed={4} motifSeed={2} />
      )}
      <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
        {p.heading && <Eyebrow><span className="text-neutral/80" data-testid="login-side-heading">{p.heading}</span></Eyebrow>}
        {p.supporting_text && <p className="text-[14px] text-white/80 mt-4 max-w-[46ch] leading-relaxed" data-testid="login-side-text">{p.supporting_text}</p>}
        {p.quote && (
          <blockquote className="font-display text-[28px] leading-[1.15] tracking-[-0.01em] mt-6" data-testid="login-side-quote">
            “{p.quote}”
          </blockquote>
        )}
        {(p.attribution_name || p.attribution_role) && (
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full" style={{ background: "linear-gradient(135deg,#9C7B98,#5a3a55)" }} />
            <div className="flex flex-col leading-tight">
              {p.attribution_name && <b className="text-[13px] font-semibold" data-testid="login-side-name">{p.attribution_name}</b>}
              {p.attribution_role && <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-neutral/70">{p.attribution_role}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || null;
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const user = await login(form.email.trim(), form.password);
      const dest = nextPath ? decodeURIComponent(nextPath) : userIsAdmin(user) ? "/admin" : "/dashboard";
      nav(dest, { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section padded="sm" data-testid="login-page">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <div className="max-w-[440px] w-full mx-auto lg:mx-0 self-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-10">
              <BrandMark size={36} />
              <span className="font-display text-[20px]">Nikhil Vatika Lab</span>
            </Link>

            <Eyebrow>Welcome back</Eyebrow>
            <h1
              className="font-display text-[clamp(34px,4vw,46px)] tracking-[-0.01em] leading-[1.05] mt-4"
              data-testid="login-heading"
            >
              Continue your <AccentItalic>study</AccentItalic>.
            </h1>
            <p className="text-text-dim mt-3 text-[14.5px] leading-relaxed">
              Sign in to access your courses, sadhana progress, library and consultations.
            </p>

            {err && (
              <Alert variant="error" className="mt-6" data-testid="login-error">
                {err}
              </Alert>
            )}

            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} data-testid="login-form">
              <FormField id="email" label="Email" required>
                <div className="relative">
                  <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-faint" />
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@email.com"
                    className="pl-10"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="login-email-input"
                  />
                </div>
              </FormField>

              <FormField
                id="password"
                label="Password"
                required
                help={
                  <Link to="/forgot-password" className="text-mauve hover:underline">
                    Forgot password?
                  </Link>
                }
              >
                <div className="relative">
                  <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-faint" />
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="Your password"
                    className="pl-10"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="login-password-input"
                  />
                </div>
              </FormField>

              <Button type="submit" className="mt-2" disabled={submitting} data-testid="login-submit-btn">
                {submitting ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
              </Button>
            </form>

            <div className="mt-6 flex items-center gap-3">
              <span className="flex-1 h-px bg-line-2" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-text-faint">or</span>
              <span className="flex-1 h-px bg-line-2" />
            </div>

            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + (nextPath ? decodeURIComponent(nextPath) : "/dashboard");
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="login-google-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-full border border-line-2 bg-surface hover:border-mauve hover:bg-glass-2 transition-colors text-[14px]"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-[13.5px] text-text-dim mt-6 text-center">
              New to NVL?{" "}
              <Link to="/register" className="text-mauve font-medium hover:underline">
                Create an account
              </Link>
            </p>
          </div>

          <LoginSidePanel />
        </div>
      </Container>
    </Section>
  );
}
